import { IApplyInfoData, IFiltersList } from "@/src/types/applicationOnline";
import { resolveApplicationStar } from "@/src/utils/applications/resolveApplicationStar";

type FilterResults = Record<string, string[] | string | null>;

const COLUMN_FILTER_KEY_TO_FIELD: Record<string, keyof IApplyInfoData> = {
  status: "form_status",
  type: "form_type",
  date: "date",
  updated: "updated",
  country: "country",
  course: "course",
  assignee: "assignee",
  color_star: "color_star",
  form_status: "form_status",
  form_type: "form_type",
};

const toComparableDate = (value: string): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getFilterString = (value: string[] | string | null | undefined): string | null =>
  typeof value === "string" ? value : null;

const uniqueStrings = (values: unknown[]): string[] =>
  Array.from(new Set(values.map((v) => String(v ?? "").trim()).filter((v) => v !== "")));

const matchesDateRange = (rowValue: string, startKey: string, endKey: string, filters: FilterResults): boolean => {
  const start = getFilterString(filters[startKey]);
  const end = getFilterString(filters[endKey]);
  if (!start && !end) return true;

  const rowDate = toComparableDate(rowValue);
  if (rowDate == null) return false;

  const startDate = start ? toComparableDate(start) : null;
  const endDate = end ? toComparableDate(end) : null;

  if (startDate != null && rowDate < startDate) return false;
  if (endDate != null && rowDate > endDate) return false;
  return true;
};

const matchesQuery = (row: IApplyInfoData, query: string): boolean => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;

  const haystack = [
    row.fullname,
    row.email,
    row.phone,
    row.country,
    row.course,
    row.form_status,
    row.form_type,
    row.assignee,
    String(row.id),
    String(row.applicant_id),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(normalized);
};

export const buildApplicationsFiltersList = (rows: IApplyInfoData[]): IFiltersList => {
  return {
    type: uniqueStrings(rows.map((row) => row.form_type)) as [],
    status: uniqueStrings(rows.map((row) => row.form_status)) as [],
    course: uniqueStrings(rows.map((row) => row.course)) as [],
    assignee: uniqueStrings(rows.map((row) => row.assignee ?? "")) as [],
    is_read: uniqueStrings(rows.map((row) => (row.is_read ? "true" : "false"))) as [],
    color_star: uniqueStrings(rows.map((row) => row.color_star)) as [],
    country: uniqueStrings(rows.map((row) => row.country)) as [],
  };
};

export const mergeApplicationsFiltersList = (apiList?: IFiltersList, dataList?: IFiltersList): IFiltersList | undefined => {
  if (!apiList && !dataList) return undefined;

  const mergeValues = (key: keyof IFiltersList) => {
    const fromApi = Array.isArray(apiList?.[key]) ? apiList![key].map(String) : [];
    const fromData = Array.isArray(dataList?.[key]) ? dataList![key].map(String) : [];
    return Array.from(new Set([...fromApi, ...fromData])) as IFiltersList[typeof key];
  };

  return {
    type: mergeValues("type"),
    status: mergeValues("status"),
    course: mergeValues("course"),
    assignee: mergeValues("assignee"),
    is_read: mergeValues("is_read"),
    color_star: mergeValues("color_star"),
    country: mergeValues("country"),
  };
};

export const filterApplicationsRows = (rows: IApplyInfoData[], filters: FilterResults): IApplyInfoData[] => {
  return rows.filter((row) => {
    for (const [key, value] of Object.entries(filters)) {
      if (value == null || value === "") continue;
      if (Array.isArray(value) && value.length === 0) continue;

      if (key === "query") {
        if (!matchesQuery(row, String(value))) return false;
        continue;
      }

      if (key === "start_date" || key === "end_date") continue;
      if (key === "start_updated_date" || key === "end_updated_date") continue;

      const fieldKey = COLUMN_FILTER_KEY_TO_FIELD[key] ?? (key as keyof IApplyInfoData);

      if (Array.isArray(value)) {
        const rowValue =
          fieldKey === "color_star"
            ? resolveApplicationStar(String(row.color_star ?? "")).color
            : String(row[fieldKey] ?? "");
        if (!value.map(String).includes(rowValue)) return false;
        continue;
      }

      if (String(row[fieldKey] ?? "") !== String(value)) return false;
    }

    if (!matchesDateRange(row.date, "start_date", "end_date", filters)) return false;
    if (!matchesDateRange(row.updated, "start_updated_date", "end_updated_date", filters)) return false;

    return true;
  });
};
